import { useCallback, useEffect, useState } from "react";
import { Room, RemoteParticipant } from "twilio-video";
import * as Sentry from "@sentry/react";
import { getParticipantType } from "src/services/qr-participant.service";
import { useToast } from "src/context/ToastContext";
import AddinCallIconWhite from "../assets/Room/add-call-white.svg";
import { ParticipantType } from "src/types/participant.type";
import EventTracker from "src/pendo/EventTracker";
import { useCall } from "src/call-sdk/context/CallContext";

enum Action {
  Connected,
  Disconnected,
}

// Custom hook to get participants from a room
const useParticipants = (room: Room | null, requestId: string | null) => {
  const [participants, setParticipants] = useState<RemoteParticipant[]>([]);
  const [hearingParticipants, setHearingParticipants] = useState<
    RemoteParticipant[]
  >([]);

  const toast = useToast();
  const { setVco, toggleHuQRDialog } = useCall();

  const getActiveInterpreterIds = useCallback(async (): Promise<string[]> => {
    if (!room) return [];
    const ids: string[] = [];

    for (const p of room.participants.values()) {
      const { identity } = p;
      const { data } = await getParticipantType({
        requestId,
        participantId: identity,
      });
      if (
        data?.participantType === ParticipantType.AssignedInterpreter ||
        data?.participantType === ParticipantType.TransferInterpreter
      ) {
        ids.push(data.participantId ?? identity);
      }
    }
    return ids;
  }, [room, requestId]);

  const InitalUpdateParticipants = useCallback(async () => {
    if (room) {
      const participantArray = Array.from(room.participants.values());
      setParticipants(participantArray);
    }
  }, [room]);

  // Define a function to update the participants
  const updateParticipants = useCallback(
    async (participant: RemoteParticipant, action: Action) => {
      if (room) {
        const { identity } = participant || {};
        const { data } = await getParticipantType({
          requestId,
          participantId: identity || "",
        });
        if (data?.participantType === ParticipantType.HuQrUser) {
          setHearingParticipants((prevParticipants) =>
            action === Action.Connected
              ? [...prevParticipants, participant]
              : prevParticipants.filter((p) => p !== participant)
          );
          if (action === Action.Connected) {
            room.localParticipant.audioTracks.forEach((publication) => {
              publication.track.disable();
            });

            setVco(false);
            toggleHuQRDialog(false);

            toast.success(
              "The hearing person is now connected! Your mic and speaker are muted.",
              { prefixIcon: AddinCallIconWhite }
            );

            const interpreterIds = await getActiveInterpreterIds();
            EventTracker.trackEvent("hearingUserConnectedAudio", {
              requestId,
              interpreterIds,
            });
          }
        } else {
          setParticipants((prevParticipants) =>
            action === Action.Connected
              ? [...prevParticipants, participant]
              : prevParticipants.filter((p) => p !== participant)
          );
        }

        const participantArray = Array.from(room.participants.values());
        //log participant count to sentry
        Sentry.captureMessage(
          `Participants updated:${participantArray.length}`
        );
        //log each participant to sentry
        Sentry.captureMessage(
          `Participants currently in room :${participantArray
            .map((p) => p.identity)
            .join(", ")}`
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [room, requestId, getActiveInterpreterIds]
  );

  useEffect(() => {
    if (room) {
      InitalUpdateParticipants(); // Initial update

      //listen for participant connected events
      const handleParticipantConnected = async (
        participant: RemoteParticipant
      ) => {
        Sentry.captureMessage(`participant connected:${participant.identity}`);
        await updateParticipants(participant, Action.Connected);
      };
      room?.on("participantConnected", handleParticipantConnected);

      //listen for participnat disconnected events
      const handleParticipantDisconnected = async (
        participant: RemoteParticipant
      ) => {
        console.log("participant disconnect ", participant);
        Sentry.captureMessage(
          `participant disconnected:${participant.identity}`
        );
        await updateParticipants(participant, Action.Disconnected);
      };
      room?.on("participantDisconnected", handleParticipantDisconnected);

      // Clean up event listeners when the component unmounts
      return () => {
        room?.off("participantConnected", handleParticipantConnected);
        room?.off("participantDisconnected", handleParticipantDisconnected);
      };
    }
  }, [room, updateParticipants, InitalUpdateParticipants]);

  return { participants, hearingParticipants };
};

export default useParticipants;
